import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { toast } from 'sonner';
import type { OperatorTaskListItemResponse } from '@/core/generated/api';
import { operatorTaskService, OperatorTaskConflictError } from '@/entities/OperatorTask/api/operatorTaskService';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { getErrorMessage } from '@/shared/lib/errors';
import { hasUnsavedChanges } from '@/shared/model/unsavedChanges';
import { OPERATOR_TASK_FORM_GROUP } from './constants';

/** Отмечает выполнение задачи или возвращает её в работу */
export const useTaskCompletion = (scopeKey = '') => {
    const scopeRef = useRef(scopeKey);
    scopeRef.current = scopeKey;
    const pendingRef = useRef(new Set<string>());
    const [pending, setPending] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { setError(null); }, [scopeKey]);

    const changeStatus = useCallback(async (task: OperatorTaskListItemResponse, reopen = false) => {
        if (!task.id || !task.version || pendingRef.current.has(task.id)) return;

        const id = task.id;
        const scope = scopeRef.current;
        pendingRef.current.add(id);
        setPending([...pendingRef.current]);
        setError(null);

        try {
            if (reopen) await operatorTaskService.reopen(id, task.version);
            else await operatorTaskService.complete(id, task.version);

            toast.success(reopen
                ? 'Задача возвращена в работу'
                : 'Задача выполнена');

            const store = useOperatorTasks.getState();

            store.invalidateNotifications();
            await store.fetchList();

            if (useOperatorTasks.getState().current?.id === id && !hasUnsavedChanges(OPERATOR_TASK_FORM_GROUP)) {
                await Promise.all([store.fetchDetails(id), store.fetchHistory(id)]);
            }
        } catch (reason: unknown) {
            if (scope === scopeRef.current) setError(getErrorMessage(reason, 'Не удалось изменить статус задачи'));
            if (reason instanceof OperatorTaskConflictError) {
                const store = useOperatorTasks.getState();
                store.invalidateNotifications();
                await store.fetchList();

                if (useOperatorTasks.getState().current?.id === id && !hasUnsavedChanges(OPERATOR_TASK_FORM_GROUP)) {
                    await Promise.all([store.fetchDetails(id), store.fetchHistory(id)]);
                }
            }
        } finally {
            pendingRef.current.delete(id);
            setPending([...pendingRef.current]);
        }
    }, []);

    return {
        pending,
        error,
        changeStatus
    };
};
